/**
 * Trade Log Chart Components
 * Simple line charts for trade log statistics
 */

import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useBreakpointValue } from "@chakra-ui/react";
import { Trade } from "../../../../types/trade";

// Helper function to calculate time series data from trades
const calculateTimeSeriesData = (
  trades: Trade[],
  getValue: (trade: Trade) => number,
  getDate: (trade: Trade) => Date,
  normalizeToPercent: boolean = true
): number[] => {
  if (trades.length === 0) {
    return [0, 0, 0, 0, 0, 0, 0, 0];
  }

  // Sort trades by date
  const sortedTrades = [...trades]
    .filter(trade => getValue(trade) !== undefined && getValue(trade) !== null)
    .sort((a, b) => getDate(a).getTime() - getDate(b).getTime());

  if (sortedTrades.length === 0) {
    return [0, 0, 0, 0, 0, 0, 0, 0];
  }

  // Group trades into 8 time periods
  const periods = 8;
  const firstDate = getDate(sortedTrades[0]);
  const lastDate = getDate(sortedTrades[sortedTrades.length - 1]);
  const timeRange = lastDate.getTime() - firstDate.getTime();
  const periodDuration = timeRange / (periods - 1);

  const periodValues: number[] = [];
  let cumulativeValue = 0;

  for (let i = 0; i < periods; i++) {
    const periodEndTime = firstDate.getTime() + (periodDuration * i);
    const periodEndDate = new Date(periodEndTime);

    // Sum all trades up to this period
    const tradesInPeriod = sortedTrades.filter(trade => {
      const tradeDate = getDate(trade);
      return tradeDate <= periodEndDate;
    });

    if (i === 0) {
      // First period: just the trades in that period
      cumulativeValue = tradesInPeriod.reduce((sum, trade) => sum + getValue(trade), 0);
    } else {
      // Subsequent periods: cumulative value
      cumulativeValue = tradesInPeriod.reduce((sum, trade) => sum + getValue(trade), 0);
    }

    periodValues.push(cumulativeValue);
  }

  // Normalize to 0-100 range if needed
  if (normalizeToPercent) {
    const maxValue = Math.max(...periodValues, 1);
    const minValue = Math.min(...periodValues, 0);
    const range = maxValue - minValue || 1;
    
    return periodValues.map(v => {
      const normalized = ((v - minValue) / range) * 100;
      return Math.max(0, Math.min(100, normalized));
    });
  }

  return periodValues;
};

// Reusable chart component
interface SimpleLineChartProps {
  value: number;
  trades: Trade[];
  useWhiteColor?: boolean;
  color?: string;
  name?: string;
  getValue: (trade: Trade) => number;
  getDate: (trade: Trade) => Date;
}

const SimpleLineChart: React.FC<SimpleLineChartProps> = ({
  value,
  trades,
  useWhiteColor = false,
  color,
  name = "Value",
  getValue,
  getDate,
}) => {
  // Calculate time series data from trades
  const chartData = useMemo(() => {
    return calculateTimeSeriesData(trades, getValue, getDate, true);
  }, [trades, getValue, getDate]);

  const chartColor = useWhiteColor ? "#FFFFFF" : (color || "#38BBD1");

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: { show: false },
      animations: { enabled: true },
      padding: { top: 0, right: -10, bottom: 0, left: -10 },
      zoom: { enabled: false },
      selection: { enabled: false },
      dropShadow: {
        enabled: true,
        color: chartColor,
        top: 0,
        left: 0,
        blur: 4,
        opacity: 0.5,
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: [chartColor],
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [chartColor],
        inverseColors: false,
        opacityFrom: 0,
        opacityTo: 0,
        stops: [0, 100],
      },
      colors: [chartColor],
    },
    colors: [chartColor],
    dataLabels: { enabled: false },
    grid: {
      show: false,
      padding: { top: -15, right: -15, bottom: -15, left: -15 },
    },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
    },
    yaxis: {
      labels: { show: false },
      min: 0,
      max: 100,
      forceNiceScale: false,
      crosshairs: { show: false },
    },
    tooltip: { enabled: false },
    legend: { show: false },
    plotOptions: {
      area: {
        fillTo: "end",
      },
    },
  }), [chartColor]);

  const series = useMemo(() => [
    {
      name,
      data: chartData,
    },
  ], [chartData, name]);

  const chartHeight = useBreakpointValue({ base: 30, sm: 38, md: 45 }) || 38;

  return (
    <ReactApexChart
      options={chartOptions}
      series={series}
      type="area"
      height={chartHeight}
      width="100%"
    />
  );
};

// Total Trades Chart
export const TotalTradesChart: React.FC<{ totalTrades: number; trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  totalTrades,
  trades,
  useWhiteColor = false 
}) => (
  <SimpleLineChart 
    value={totalTrades} 
    trades={trades}
    useWhiteColor={useWhiteColor} 
    name="Total Trades"
    getValue={() => 1}
    getDate={(trade) => new Date(trade.entryDate)}
  />
);

// Active Trades Chart
export const OpenTradesChart: React.FC<{ openTrades: number; trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  openTrades,
  trades,
  useWhiteColor = false 
}) => (
  <SimpleLineChart 
    value={openTrades} 
    trades={trades.filter(t => t.status === "open")}
    useWhiteColor={useWhiteColor} 
    color="#38BBD1" 
    name="Active Trades"
    getValue={() => 1}
    getDate={(trade) => new Date(trade.entryDate)}
  />
);

// Win Rate Chart - shows cumulative win rate over time
export const WinRateChart: React.FC<{ winRate: number; trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  winRate,
  trades,
  useWhiteColor = false 
}) => {
  const chartData = useMemo(() => {
    const closedTrades = trades.filter(t => t.status === "closed" && t.netPnL !== undefined);
    if (closedTrades.length === 0) return [0, 0, 0, 0, 0, 0, 0, 0];

    const sortedTrades = [...closedTrades].sort((a, b) => {
      const dateA = new Date(a.exitDate || a.entryDate).getTime();
      const dateB = new Date(b.exitDate || b.entryDate).getTime();
      return dateA - dateB;
    });

    const periods = 8;
    const firstDate = new Date(sortedTrades[0].exitDate || sortedTrades[0].entryDate);
    const lastDate = new Date(sortedTrades[sortedTrades.length - 1].exitDate || sortedTrades[sortedTrades.length - 1].entryDate);
    const timeRange = lastDate.getTime() - firstDate.getTime();
    const periodDuration = timeRange / (periods - 1);

    const periodWinRates: number[] = [];

    for (let i = 0; i < periods; i++) {
      const periodEndTime = firstDate.getTime() + (periodDuration * i);
      const periodEndDate = new Date(periodEndTime);

      const tradesUpToPeriod = sortedTrades.filter(trade => {
        const tradeDate = new Date(trade.exitDate || trade.entryDate);
        return tradeDate <= periodEndDate;
      });

      if (tradesUpToPeriod.length === 0) {
        periodWinRates.push(0);
      } else {
        const winningTrades = tradesUpToPeriod.filter(t => (t.netPnL || 0) > 0).length;
        const winRate = (winningTrades / tradesUpToPeriod.length) * 100;
        periodWinRates.push(winRate);
      }
    }

    return periodWinRates;
  }, [trades]);

  const chartColor = useWhiteColor ? "#FFFFFF" : "#17FEAE";
  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: { show: false },
      animations: { enabled: true },
      padding: { top: 0, right: -10, bottom: 0, left: -10 },
      zoom: { enabled: false },
      selection: { enabled: false },
      dropShadow: {
        enabled: true,
        color: chartColor,
        top: 0,
        left: 0,
        blur: 4,
        opacity: 0.5,
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: [chartColor],
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [chartColor],
        inverseColors: false,
        opacityFrom: 0,
        opacityTo: 0,
        stops: [0, 100],
      },
      colors: [chartColor],
    },
    colors: [chartColor],
    dataLabels: { enabled: false },
    grid: {
      show: false,
      padding: { top: -15, right: -15, bottom: -15, left: -15 },
    },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
    },
    yaxis: {
      labels: { show: false },
      min: 0,
      max: 100,
      forceNiceScale: false,
      crosshairs: { show: false },
    },
    tooltip: { enabled: false },
    legend: { show: false },
    plotOptions: {
      area: {
        fillTo: "end",
      },
    },
  }), [chartColor]);

  const series = useMemo(() => [
    {
      name: "Win Rate",
      data: chartData,
    },
  ], [chartData]);

  const chartHeight = useBreakpointValue({ base: 30, sm: 38, md: 45 }) || 38;

  return (
    <ReactApexChart
      options={chartOptions}
      series={series}
      type="area"
      height={chartHeight}
      width="100%"
    />
  );
};

// Total P&L Chart
export const TotalPnLChart: React.FC<{ totalPnL: number; trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  totalPnL,
  trades,
  useWhiteColor = false 
}) => (
  <SimpleLineChart 
    value={totalPnL} 
    trades={trades.filter(t => t.status === "closed" && t.netPnL !== undefined)}
    useWhiteColor={useWhiteColor} 
    color={totalPnL >= 0 ? "#17FEAE" : "#EF4444"} 
    name="Total P&L"
    getValue={(trade) => trade.netPnL || 0}
    getDate={(trade) => new Date(trade.exitDate || trade.entryDate)}
  />
);

// Closed Trades Chart
export const ClosedTradesChart: React.FC<{ closedTrades: number; trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  closedTrades,
  trades,
  useWhiteColor = false 
}) => (
  <SimpleLineChart 
    value={closedTrades} 
    trades={trades.filter(t => t.status === "closed")}
    useWhiteColor={useWhiteColor} 
    name="Closed Trades"
    getValue={() => 1}
    getDate={(trade) => new Date(trade.exitDate || trade.entryDate)}
  />
);

// Average Win Chart - shows average win over time
export const AverageWinChart: React.FC<{ averageWin: number; trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  averageWin,
  trades,
  useWhiteColor = false 
}) => {
  const chartData = useMemo(() => {
    const winningTrades = trades.filter(t => t.status === "closed" && (t.netPnL || 0) > 0);
    if (winningTrades.length === 0) return [0, 0, 0, 0, 0, 0, 0, 0];

    const sortedTrades = [...winningTrades].sort((a, b) => {
      const dateA = new Date(a.exitDate || a.entryDate).getTime();
      const dateB = new Date(b.exitDate || b.entryDate).getTime();
      return dateA - dateB;
    });

    const periods = 8;
    const firstDate = new Date(sortedTrades[0].exitDate || sortedTrades[0].entryDate);
    const lastDate = new Date(sortedTrades[sortedTrades.length - 1].exitDate || sortedTrades[sortedTrades.length - 1].entryDate);
    const timeRange = lastDate.getTime() - firstDate.getTime();
    const periodDuration = timeRange / (periods - 1);

    const periodAverages: number[] = [];

    for (let i = 0; i < periods; i++) {
      const periodEndTime = firstDate.getTime() + (periodDuration * i);
      const periodEndDate = new Date(periodEndTime);

      const tradesUpToPeriod = sortedTrades.filter(trade => {
        const tradeDate = new Date(trade.exitDate || trade.entryDate);
        return tradeDate <= periodEndDate;
      });

      if (tradesUpToPeriod.length === 0) {
        periodAverages.push(0);
      } else {
        const totalWin = tradesUpToPeriod.reduce((sum, t) => sum + (t.netPnL || 0), 0);
        const avgWin = totalWin / tradesUpToPeriod.length;
        periodAverages.push(avgWin);
      }
    }

    // Normalize to 0-100
    const maxValue = Math.max(...periodAverages, 1);
    const minValue = Math.min(...periodAverages, 0);
    const range = maxValue - minValue || 1;
    return periodAverages.map(v => ((v - minValue) / range) * 100);
  }, [trades]);

  const chartColor = useWhiteColor ? "#FFFFFF" : "#17FEAE";
  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: { show: false },
      animations: { enabled: true },
      padding: { top: 0, right: -10, bottom: 0, left: -10 },
      zoom: { enabled: false },
      selection: { enabled: false },
      dropShadow: {
        enabled: true,
        color: chartColor,
        top: 0,
        left: 0,
        blur: 4,
        opacity: 0.5,
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: [chartColor],
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [chartColor],
        inverseColors: false,
        opacityFrom: 0,
        opacityTo: 0,
        stops: [0, 100],
      },
      colors: [chartColor],
    },
    colors: [chartColor],
    dataLabels: { enabled: false },
    grid: {
      show: false,
      padding: { top: -15, right: -15, bottom: -15, left: -15 },
    },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
    },
    yaxis: {
      labels: { show: false },
      min: 0,
      max: 100,
      forceNiceScale: false,
      crosshairs: { show: false },
    },
    tooltip: { enabled: false },
    legend: { show: false },
    plotOptions: {
      area: {
        fillTo: "end",
      },
    },
  }), [chartColor]);

  const series = useMemo(() => [
    {
      name: "Average Win",
      data: chartData,
    },
  ], [chartData]);

  const chartHeight = useBreakpointValue({ base: 30, sm: 38, md: 45 }) || 38;

  return (
    <ReactApexChart
      options={chartOptions}
      series={series}
      type="area"
      height={chartHeight}
      width="100%"
    />
  );
};

// Largest Win Chart - shows trend of largest wins over time
export const LargestWinChart: React.FC<{ largestWin: number; trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  largestWin,
  trades,
  useWhiteColor = false 
}) => {
  const chartData = useMemo(() => {
    const winningTrades = trades.filter(t => t.status === "closed" && (t.netPnL || 0) > 0);
    if (winningTrades.length === 0) return [0, 0, 0, 0, 0, 0, 0, 0];

    const sortedTrades = [...winningTrades].sort((a, b) => {
      const dateA = new Date(a.exitDate || a.entryDate).getTime();
      const dateB = new Date(b.exitDate || b.entryDate).getTime();
      return dateA - dateB;
    });

    const periods = 8;
    const firstDate = new Date(sortedTrades[0].exitDate || sortedTrades[0].entryDate);
    const lastDate = new Date(sortedTrades[sortedTrades.length - 1].exitDate || sortedTrades[sortedTrades.length - 1].entryDate);
    const timeRange = lastDate.getTime() - firstDate.getTime();
    const periodDuration = timeRange / (periods - 1);

    const periodMaxWins: number[] = [];

    for (let i = 0; i < periods; i++) {
      const periodEndTime = firstDate.getTime() + (periodDuration * i);
      const periodEndDate = new Date(periodEndTime);

      const tradesUpToPeriod = sortedTrades.filter(trade => {
        const tradeDate = new Date(trade.exitDate || trade.entryDate);
        return tradeDate <= periodEndDate;
      });

      if (tradesUpToPeriod.length === 0) {
        periodMaxWins.push(0);
      } else {
        const maxWin = Math.max(...tradesUpToPeriod.map(t => t.netPnL || 0));
        periodMaxWins.push(maxWin);
      }
    }

    // Normalize to 0-100
    const maxValue = Math.max(...periodMaxWins, 1);
    return periodMaxWins.map(v => (v / maxValue) * 100);
  }, [trades]);

  const chartColor = useWhiteColor ? "#FFFFFF" : "#17FEAE";
  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: { show: false },
      animations: { enabled: true },
      padding: { top: 0, right: -10, bottom: 0, left: -10 },
      zoom: { enabled: false },
      selection: { enabled: false },
      dropShadow: {
        enabled: true,
        color: chartColor,
        top: 0,
        left: 0,
        blur: 4,
        opacity: 0.5,
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: [chartColor],
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [chartColor],
        inverseColors: false,
        opacityFrom: 0,
        opacityTo: 0,
        stops: [0, 100],
      },
      colors: [chartColor],
    },
    colors: [chartColor],
    dataLabels: { enabled: false },
    grid: {
      show: false,
      padding: { top: -15, right: -15, bottom: -15, left: -15 },
    },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
    },
    yaxis: {
      labels: { show: false },
      min: 0,
      max: 100,
      forceNiceScale: false,
      crosshairs: { show: false },
    },
    tooltip: { enabled: false },
    legend: { show: false },
    plotOptions: {
      area: {
        fillTo: "end",
      },
    },
  }), [chartColor]);

  const series = useMemo(() => [
    {
      name: "Largest Win",
      data: chartData,
    },
  ], [chartData]);

  const chartHeight = useBreakpointValue({ base: 30, sm: 38, md: 45 }) || 38;

  return (
    <ReactApexChart
      options={chartOptions}
      series={series}
      type="area"
      height={chartHeight}
      width="100%"
    />
  );
};

// Most Traded Chart - shows trend of most traded symbol over time
export const MostTradedChart: React.FC<{ trades: Trade[]; useWhiteColor?: boolean }> = ({ 
  trades,
  useWhiteColor = false 
}) => (
  <SimpleLineChart 
    value={50} 
    trades={trades}
    useWhiteColor={useWhiteColor} 
    color="#38BBD1" 
    name="Most Traded"
    getValue={() => 1}
    getDate={(trade) => new Date(trade.entryDate)}
  />
);

